/**
 * api.ts — typed fetch client for the explorer backend.
 *
 * All calls go through the `/api` prefix, which both the Vite dev server
 * (vite.config.ts) and nginx (nginx.conf) proxy to the FastAPI service under
 * `/v1`. A request to `/api/coverage/summary` therefore reaches
 * `GET /v1/coverage/summary` in both dev and prod — no extra config needed.
 */
import type {
  CoverageByDmaResponse,
  CoverageSummaryResponse,
  OnboardingPayload,
} from "../types/realsynq";

const BASE = "/api";
const DEFAULT_TIMEOUT_MS = 8000;

/** Error thrown for non-2xx responses, carrying status + RFC7807 detail when present. */
export class ApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

interface FetchOpts {
  method?: string;
  body?: unknown;
  timeoutMs?: number;
  /** Internal: how many retries remain (network error / 5xx only). */
  _retries?: number;
}

async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const { method = "GET", body, timeoutMs = DEFAULT_TIMEOUT_MS, _retries = 1 } = opts;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    // Retry once on server errors; 4xx are deterministic — surface immediately.
    if (res.status >= 500 && _retries > 0) {
      clearTimeout(timer);
      return apiFetch<T>(path, { ...opts, _retries: _retries - 1 });
    }

    if (!res.ok) {
      let detail: string | undefined;
      try {
        const problem = await res.json();
        detail = problem?.detail ?? problem?.title;
      } catch {
        /* response had no JSON body */
      }
      throw new ApiError(`Request failed: ${res.status}`, res.status, detail);
    }

    return (await res.json()) as T;
  } catch (err) {
    // Network error / abort → retry once, then rethrow.
    if (!(err instanceof ApiError) && _retries > 0) {
      return apiFetch<T>(path, { ...opts, _retries: _retries - 1 });
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export function getCoverageSummary(lookbackDays: number): Promise<CoverageSummaryResponse> {
  return apiFetch<CoverageSummaryResponse>(`/coverage/summary?lookback_days=${lookbackDays}`);
}

export function getCoverageByDma(
  lookbackDays: number,
  limit = 20,
): Promise<CoverageByDmaResponse> {
  return apiFetch<CoverageByDmaResponse>(
    `/coverage/by-dma?lookback_days=${lookbackDays}&limit=${limit}`,
  );
}

/** Submit an onboarding seat-mapping request. Mirrors the prior inline fetch in main.tsx. */
export function submitOnboarding(payload: OnboardingPayload): Promise<void> {
  return apiFetch<unknown>("/onboarding", { method: "POST", body: payload }).then(() => undefined);
}
